import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useT } from "@/i18n/LanguageContext";
import { STAGE_LABEL } from "@/constants/roles";

const ago = (iso) => {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "today" : d === 1 ? "1 day ago" : `${d} days ago`;
};

/** 🔔 Dashboard square: reminders due for my role. Press → list; ✓ Done finishes one and the count drops by 1. */
export function RemindersSquare() {
  const { t } = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: due = [] } = useQuery({
    queryKey: ["reminders-due"],
    queryFn: async () => (await api.get("/order-reminders/due")).data,
    refetchInterval: 60_000,
  });
  const done = useMutation({
    mutationFn: (rid) => api.post(`/order-reminders/${rid}/done`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reminders-due"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
      toast.success(t("Reminder finished"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t("Could not finish the reminder")),
  });
  const n = due.length;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`text-left border rounded-md p-4 transition-colors block w-full ${n ? "bg-amber-50 border-amber-300 hover:border-amber-500" : "bg-white border-slate-200 hover:border-slate-400"}`}
        data-testid="stage-stat-reminders"
      >
        <div className={`text-[15px] font-bold uppercase tracking-wider font-mono leading-tight ${n ? "text-amber-800" : "text-slate-600"}`} data-testid="stage-stat-label-reminders">🔔 {t("Reminders")}</div>
        <div className={`font-display text-[45px] font-bold mt-1 leading-none ${n ? "text-amber-900" : ""}`} data-testid="stage-stat-count-reminders">{n}</div>
        {n ? <div className="text-[11px] font-mono text-amber-800">{t("press to see the list")}</div> : null}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="reminders-dialog">
          <DialogHeader>
            <DialogTitle>🔔 {t("Reminders")} · {n}</DialogTitle>
            <DialogDescription>{t("Reminders addressed to your role that are due. ✓ Done removes one from your count.")}</DialogDescription>
          </DialogHeader>
          {n === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center" data-testid="reminders-empty">{t("Nothing due — all reminders finished.")}</div>
          ) : (
            <ul className="divide-y divide-slate-200" data-testid="reminders-list">
              {due.map((r) => (
                <li key={r.id} className="py-3 flex items-start gap-3" data-testid={`reminder-row-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/order/${r.order_id}`} className="font-mono font-semibold text-slate-900 underline" data-testid={`reminder-open-${r.id}`}>{r.po_number || r.order?.po_number || "—"}</Link>
                      {r.order?.status && <span className="text-[11px] font-mono uppercase text-slate-500">{t(STAGE_LABEL[r.order.status] || r.order.status)}</span>}
                      <span className="text-[11px] font-mono text-amber-700">{t("due")} {ago(r.remind_at)}</span>
                    </div>
                    <div className="text-sm text-slate-800 mt-0.5" data-testid={`reminder-reason-${r.id}`}>{r.reason}</div>
                    {r.note && <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap">{r.note}</div>}
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {[r.order?.brand, r.order?.model, r.order?.year].filter(Boolean).join(" · ")}{r.created_by_name ? ` · ${t("set by")} ${r.created_by_name}` : ""}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                    disabled={done.isPending}
                    onClick={() => done.mutate(r.id)}
                    data-testid={`reminder-done-${r.id}`}
                  >
                    ✓ {t("Done")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
